// Polished top bar (MASTER_PROMPT §2.2):
//
//   [Hamburger]  [Search pill — opens Mod+K]   [+ Create] [SendStatus] [Bell] [Theme] [Profile]
//
// 48 px tall. Search pill is a dummy that triggers the command palette.
// Theme toggle is now visible in the bar (not buried in profile menu)
// per the new spec. The send-status pill stays as a calm presence so the
// operator always knows whether sequences are sending healthy.

import { useState } from "react";
import { Search, HelpCircle, Menu } from "lucide-react";
import NotificationsBell from "./notifications-bell";
import ProfileMenu from "./profile-menu";
import CommandPalette from "./command-palette";
import SendStatusPill from "./send-status-pill";
import ThemeToggle from "./theme-toggle";
import CreateMenu from "./create-menu";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useModKey, useAriaKeyshortcut } from "@/hooks/useModKey";

interface Props {
  onOpenDrawer?: () => void;
  onOpenHelp?: () => void;
}

export default function Topbar({ onOpenDrawer, onOpenHelp }: Props) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const mod = useModKey();
  const kbdAria = useAriaKeyshortcut("K");

  useKeyboardShortcuts(
    [
      { key: "k", meta: true, force: true, handler: () => setPaletteOpen((o) => !o) },
      { key: "/", handler: () => setPaletteOpen(true) },
    ],
    true,
  );

  return (
    <>
      <header className="flex h-[48px] shrink-0 items-center gap-2 border-b border-border-soft bg-bg-app px-3 sm:gap-3 sm:px-4">
        {onOpenDrawer && (
          <button
            onClick={onOpenDrawer}
            className="rounded p-1.5 text-text-2 hover:bg-bg-hover hover:text-text md:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}

        <button
          onClick={() => setPaletteOpen(true)}
          className="group flex h-8 max-w-md flex-1 items-center gap-2 rounded-md border border-border bg-bg-input pl-3 pr-2 text-base text-text-3 transition-colors duration-[120ms] ease-lz hover:border-accent/40 hover:bg-bg-hover hover:text-text focus:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/35"
        >
          <Search className="h-3.5 w-3.5 shrink-0" />
          <span className="hidden flex-1 truncate text-left sm:inline">Search records, actions…</span>
          <span className="flex-1 truncate text-left sm:hidden">Search</span>
          <span
            className="hidden shrink-0 items-center gap-0.5 rounded border border-border bg-bg-card px-1.5 py-0.5 font-mono text-[10px] text-text-3 md:inline-flex"
            aria-keyshortcuts={kbdAria}
          >
            <span>{mod}</span><span>K</span>
          </span>
        </button>

        {/* Push the right cluster to the edge */}
        <div className="hidden flex-1 sm:block" />

        <div className="flex shrink-0 items-center gap-1">
          <CreateMenu />
          <SendStatusPill />
          {onOpenHelp && (
            <button
              onClick={onOpenHelp}
              className="hidden rounded p-1.5 text-text-2 hover:bg-bg-hover hover:text-text md:block"
              aria-label="Keyboard shortcuts"
              title="Shortcuts (?)"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          )}
          <NotificationsBell />
          <ThemeToggle />
          <div className="mx-1 hidden h-5 w-px bg-border sm:block" />
          <ProfileMenu />
        </div>
      </header>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  );
}
